import { useEffect, useState } from 'react';

export interface SiteContact {
  supportEmail: string;
  supportWhatsapp: string;
}

export interface SiteInfo extends SiteContact {
  /** Tema global escolhido em Administração › Configurações (`''` quando a API é antiga). */
  theme: string;
}

const FALLBACK: SiteContact = { supportEmail: 'sketch.inc26@gmail.com', supportWhatsapp: '5564993265529' };
const FALLBACK_INFO: SiteInfo = { ...FALLBACK, theme: '' };

let cache: SiteInfo | null = null;
let inflight: Promise<SiteInfo> | null = null;

function parse(data: Partial<SiteInfo> | null): SiteInfo {
  return {
    supportEmail: (data?.supportEmail ?? '').trim() || FALLBACK.supportEmail,
    supportWhatsapp: (data?.supportWhatsapp ?? '').trim() || FALLBACK.supportWhatsapp,
    theme: typeof data?.theme === 'string' ? data.theme.trim() : ''
  };
}

/** Dados públicos do site; falhas não entram no cache para permitir nova tentativa. */
export function loadSite(): Promise<SiteInfo> {
  if (cache) return Promise.resolve(cache);
  if (!inflight) {
    inflight = fetch('/api/site', { credentials: 'same-origin' })
      .then(async (res) => {
        if (!res.ok) return FALLBACK_INFO;
        const data = (await res.json().catch(() => null)) as Partial<SiteInfo> | null;
        const info = parse(data);
        cache = info;
        return info;
      })
      .catch(() => FALLBACK_INFO)
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

/** Contatos públicos definidos em Administração › Configurações. */
export function useSiteContact(): SiteContact {
  const [contact, setContact] = useState<SiteContact>(cache ?? FALLBACK);
  useEffect(() => {
    let alive = true;
    void loadSite().then((value) => {
      if (alive) setContact(value);
    });
    return () => {
      alive = false;
    };
  }, []);
  return contact;
}

export function whatsappHref(raw: string): string | null {
  const digits = raw.replace(/\D/g, '');
  if (digits.length < 10) return null;
  const withCountry = digits.startsWith('55') && digits.length >= 12 ? digits : `55${digits}`;
  return `https://wa.me/${withCountry}`;
}

export function formatWhatsapp(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  const local = digits.startsWith('55') && digits.length > 11 ? digits.slice(2) : digits;
  if (local.length === 11) return `+55 ${local.slice(0, 2)} ${local.slice(2, 7)}-${local.slice(7)}`;
  if (local.length === 10) return `+55 ${local.slice(0, 2)} ${local.slice(2, 6)}-${local.slice(6)}`;
  return raw;
}
